<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Button from 'primevue/button'
import Card from 'primevue/card'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Tag from 'primevue/tag'
import ConfirmDialog from 'primevue/confirmdialog'
import { useConfirm } from 'primevue/useconfirm'
import { useAppToast } from '@/shared/lib/useAppToast'
import { createImportJob, deleteImportJob, getImportJobs } from '../api/imports.service'
import { enumLabel, formatDate, importStatuses, stateSeverity } from '../lib/importDisplay'

const router = useRouter()
const toast = useAppToast()
const confirm = useConfirm()
const deleting = ref(false)
const loading = ref(false)
const submitting = ref(false)
const jobs = ref([])
const nextCursor = ref(null)
const cursorHistory = ref([null])
const pageIndex = ref(0)
const createVisible = ref(false)
const sourceSystem = ref('')
const mappingProfileKey = ref('')
const elapsedMs = ref(null)

const activeStatuses = new Set([2, 3, 6, 7, 'QueuedForParsing', 'Parsing', 'QueuedForApply', 'Applying'])
const canDelete = (job) => !deleting.value && !activeStatuses.has(job?.status)

const canGoBack = computed(() => pageIndex.value > 0)

async function loadJobs() {
  loading.value = true
  const started = performance.now()
  try {
    const data = await getImportJobs({
      cursor: cursorHistory.value[pageIndex.value],
      pageSize: 25
    })
    jobs.value = data.items ?? []
    nextCursor.value = data.nextCursor ?? null
  } catch (error) {
    jobs.value = []
    toast.error(error.message)
  } finally {
    elapsedMs.value = Math.round(performance.now() - started)
    loading.value = false
  }
}

async function nextPage() {
  if (!nextCursor.value) return
  cursorHistory.value = cursorHistory.value.slice(0, pageIndex.value + 1)
  cursorHistory.value.push(nextCursor.value)
  pageIndex.value += 1
  await loadJobs()
}

async function previousPage() {
  if (!canGoBack.value) return
  pageIndex.value -= 1
  await loadJobs()
}

async function createJob() {
  if (!sourceSystem.value.trim()) {
    toast.warn('Укажите источник или фабрику')
    return
  }
  submitting.value = true
  try {
    const job = await createImportJob({
      sourceSystem: sourceSystem.value.trim(),
      mappingProfileKey: mappingProfileKey.value.trim() || null
    })
    createVisible.value = false
    sourceSystem.value = ''
    mappingProfileKey.value = ''
    toast.success('Импорт создан')
    await router.push({ name: 'adminImportWorkspace', params: { jobId: job.id } })
  } catch (error) {
    toast.error(error.message)
  } finally {
    submitting.value = false
  }
}

function requestDelete(job) {
  if (!canDelete(job)) return
  confirm.require({
    header: 'Удалить запись импорта?',
    message: 'Черновики и история этого импорта будут удалены. Уже созданные товары каталога останутся без изменений.',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      if (deleting.value) return
      deleting.value = true
      try {
        await deleteImportJob(job.id)
        jobs.value = jobs.value.filter(item => item.id !== job.id)
        toast.success('Запись импорта удалена')
      } catch (error) {
        toast.error(error.message)
      } finally {
        deleting.value = false
      }
    }
  })
}

onMounted(loadJobs)
</script>

<template>
  <main class="imports-page">
    <div class="page-shell">
      <header class="page-header">
        <div>
          <Button label="Назад" icon="pi pi-arrow-left" text @click="router.push('/admin')" />
          <h1>Импорт каталогов</h1>
          <p>Загрузка, сопоставление и ручная проверка данных поставщиков</p>
        </div>
        <Button label="Новый импорт" icon="pi pi-plus" @click="createVisible = true" />
      </header>

      <Card class="content-card">
        <template #content>
          <DataTable :value="jobs" :loading="loading" data-key="id" striped-rows>
            <template #empty>
              <div class="empty-state">
                <i class="pi pi-inbox"></i>
                <strong>Импортов пока нет</strong>
                <span>Создайте первый импорт каталога.</span>
              </div>
            </template>
            <Column field="createdAt" header="Создан">
              <template #body="{ data }">{{ formatDate(data.createdAt) }}</template>
            </Column>
            <Column field="sourceSystem" header="Источник">
              <template #body="{ data }">
                <div class="primary-cell">
                  <strong>{{ data.sourceSystem || 'Без названия' }}</strong>
                  <small>{{ data.createdByName || `Пользователь ${data.createdByUserId}` }}</small>
                </div>
              </template>
            </Column>
            <Column field="status" header="Статус">
              <template #body="{ data }">
                <Tag :value="enumLabel(importStatuses, data.status)" :severity="stateSeverity(data.status)" />
              </template>
            </Column>
            <Column header="Результаты">
              <template #body="{ data }">
                <div class="result-flags">
                  <span :class="{ ready: data.activeParseAttemptId }">
                    <i class="pi pi-table"></i> {{ data.activeParseAttemptId ? 'Разбор готов' : 'Нет разбора' }}
                  </span>
                  <span :class="{ ready: data.activeDraftGenerationId }">
                    <i class="pi pi-file-edit"></i> {{ data.activeDraftGenerationId ? 'Draft активен' : 'Нет Draft' }}
                  </span>
                </div>
              </template>
            </Column>
            <Column header="">
              <template #body="{ data }">
                <Button label="Открыть" icon="pi pi-arrow-right" icon-pos="right" text
                  @click="router.push({ name: 'adminImportWorkspace', params: { jobId: data.id } })" />
                <Button label="Удалить" icon="pi pi-trash" severity="danger" text
                  :disabled="!canDelete(data)" @click="requestDelete(data)" />
              </template>
            </Column>
          </DataTable>

          <footer class="pager">
            <span>Страница {{ pageIndex + 1 }} · загрузка {{ elapsedMs ?? '—' }} ms</span>
            <div>
              <Button label="Назад" icon="pi pi-chevron-left" outlined :disabled="!canGoBack || loading" @click="previousPage" />
              <Button label="Далее" icon="pi pi-chevron-right" icon-pos="right" outlined :disabled="!nextCursor || loading" @click="nextPage" />
            </div>
          </footer>
        </template>
      </Card>
    </div>

    <Dialog v-model:visible="createVisible" modal header="Новый импорт" :style="{ width: 'min(520px, 92vw)' }">
      <div class="form-grid">
        <label>
          <span>Источник / фабрика</span>
          <InputText v-model="sourceSystem" placeholder="Например, Daniel Rubinetterie" />
        </label>
        <label>
          <span>Ключ профиля (необязательно)</span>
          <InputText v-model="mappingProfileKey" placeholder="Внутренний ключ источника" />
        </label>
      </div>
      <template #footer>
        <Button label="Отмена" text @click="createVisible = false" />
        <Button label="Создать" icon="pi pi-plus" :loading="submitting" @click="createJob" />
      </template>
    </Dialog>
    <ConfirmDialog />
  </main>
</template>

<style scoped>
.imports-page { min-height: 100vh; background: #f7f7f6; }
.page-shell { max-width: 1320px; margin: 0 auto; padding: 28px 24px 48px; }
.page-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; margin-bottom: 22px; }
.page-header h1 { margin: 8px 0 6px; font-size: clamp(28px, 4vw, 40px); letter-spacing: -.04em; }
.page-header p { margin: 0; color: #696969; }
.content-card { border: 1px solid #e6e6e3; box-shadow: 0 18px 50px rgba(20,20,20,.05); }
.primary-cell { display: grid; gap: 3px; }
.primary-cell small { color: #777; }
.result-flags { display: grid; gap: 5px; font-size: 12px; color: #929292; }
.result-flags span.ready { color: #247a45; }
.empty-state { display: grid; justify-items: center; gap: 8px; padding: 54px 20px; color: #777; }
.empty-state i { font-size: 32px; color: #bbb; }
.pager { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding-top: 20px; color: #777; font-size: 13px; }
.pager div { display: flex; gap: 8px; }
.form-grid { display: grid; gap: 18px; }
.form-grid label { display: grid; gap: 7px; font-size: 13px; font-weight: 600; }
@media (max-width: 720px) {
  .page-shell { padding: 18px 12px 32px; }
  .page-header { align-items: stretch; flex-direction: column; }
  .pager { align-items: stretch; flex-direction: column; }
  .pager div { justify-content: flex-end; }
}
</style>
